import React, {useCallback, useMemo, useState} from 'react';
import {View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useScreenTheme} from '../../../hooks/useScreenTheme';
import QuickAssistIcon from '../../../assets/icons/QuickAssist';
import {createQuickAssistBannerStyles} from './QuickAssistBanner.styles';
import Button from '../Button/Button';
import {buttonSizes, buttonVariants} from '../../../constants/global';
import {QuickAssistBannerProps} from './QuickAssistBannerProps';
import {useQuickAssist} from './useQuickAssist';
import ConfirmModal from '../../modals/ConfirmModal/ConfirmModal';

const QuickAssistBanner = ({
  customContainerStyles,
  quickAssistPayload,
}: QuickAssistBannerProps) => {
  const theme = useScreenTheme('DashboardScreen');
  const {t} = useTranslation();
  const styles = useMemo(() => createQuickAssistBannerStyles(theme), [theme]);
  const {handleQuickAssistPress} = useQuickAssist();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  /** CTA button press — show confirmation modal first */
  const onPress = useCallback(() => {
    setShowConfirmModal(true);
  }, []);

  /** User confirmed ("Yes") — proceed with Quick Assist */
  const handleConfirm = useCallback(() => {
    setShowConfirmModal(false);
    handleQuickAssistPress(quickAssistPayload);
  }, [handleQuickAssistPress, quickAssistPayload]);

  /** User declined ("No") — dismiss modal and stay */
  const handleCancel = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  return (
    <View
      style={[styles.bannerContainer, customContainerStyles]}
      accessible={false}
      accessibilityRole="none">
      <Button
        title={t('common.quickAssist.cta')}
        onPress={onPress}
        fullWidth={false}
        containerStyle={styles.ctaContainerStyle}
        showLeftIcon
        customLeftIcon={<QuickAssistIcon color={theme.viewAllCTAIconColor} />}
        size={buttonSizes.large}
        variant={buttonVariants.secondary}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={t('common.quickAssist.accessibilityLabel')}
        accessibilityHint={t('common.quickAssist.accessibilityHint')}
      />

      <ConfirmModal
        isVisible={showConfirmModal}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={t('common.quickAssist.confirmTitle')}
        description={t('common.quickAssist.confirmMessage')}
        confirmCTA={t('common.confirmModal.yes')}
        changeCTA={t('common.confirmModal.no')}
      />
    </View>
  );
};

QuickAssistBanner.displayName = 'QuickAssistBanner';

export default QuickAssistBanner;